import { flushSync } from 'react-dom';

import { IconButton, useReducedMotion, useTheme } from '@bookora/ui';

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => unknown;
};

/**
 * «روشن» ⇄ «تیره», persisted. Two states, and there is no third.
 *
 * Both themes ship on day one (SPEC §8) and this is the ONLY way into dark.
 * The panel does not read `prefers-color-scheme`: a first-time operator gets
 * light on any machine, and dark is an explicit, persisted choice made here.
 * See the inline script in `index.html` and `LightByDefault` in
 * `app/providers.tsx` for the two halves of that. The state itself lives in
 * `@bookora/ui`'s `useTheme`, which is also what mounts the AntD theme — one
 * source, so the toggle can never report a mode the `ConfigProvider` is not
 * actually in.
 *
 * ── WHY `setPreference`, NOT `toggle` ───────────────────────────────────────
 *
 * `useTheme().toggle` would do the same thing today, but what it means is
 * defined in `@bookora/ui`, which still carries the retired 'system' sentinel
 * in its `ThemePreference` union and is shared with three other surfaces. This
 * control commits a LITERAL 'light' or 'dark' so the two-state contract is
 * pinned where the contract is — a future third state added to the shared hook
 * cannot leak into this button, and `theme-default.test.tsx` can prove the
 * cycle has exactly two stops.
 *
 * The icon shows the theme you would GET, not the one you are in: a moon means
 * "switch to dark". That is the convention every OS uses and the one users
 * guess correctly.
 *
 * ── THE CROSSFADE ───────────────────────────────────────────────────────────
 *
 * Every colour in the panel changes at once, and a hard cut from paper to
 * charcoal is a flash in a dim treatment room. Where the browser supports view
 * transitions the swap is a single crossfade of the whole page on the `slow`
 * duration (the timing lives in `styles/globals.css`), and nothing moves.
 * `flushSync` makes the new theme land inside the transition's callback, which
 * is when the browser takes its "after" snapshot. Without the API, or under
 * reduced motion, the switch is instant as before.
 */
export function ThemeToggle() {
  const { mode, setPreference } = useTheme();
  const reducedMotion = useReducedMotion();

  const next = mode === 'dark' ? 'light' : 'dark';
  const nextLabel = next === 'light' ? 'روشن' : 'تیره';

  return (
    <IconButton
      type="text"
      name={next === 'light' ? 'sun' : 'moon'}
      label={`تغییر به حالت ${nextLabel}`}
      tooltipPlacement="bottom"
      onClick={() => {
        const doc = document as ViewTransitionDocument;
        if (reducedMotion || typeof doc.startViewTransition !== 'function') {
          setPreference(next);
          return;
        }
        doc.startViewTransition(() => flushSync(() => setPreference(next)));
      }}
    />
  );
}
